import React from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  useTheme as useMuiTheme,
  alpha
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle = ({ 
  variant = 'icon', // 'icon', 'button', 'switch'
  size = 'medium',
  showTooltip = true,
  sx = {}
}) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();

  const getIcon = () => {
    return isDarkMode ? <LightModeIcon /> : <DarkModeIcon />;
  };

  const getTooltipText = () => {
    return isDarkMode ? 'Light Mode\'a Geç' : 'Dark Mode\'a Geç';
  };

  const iconButtonSx = {
    color: 'inherit',
    border: `1px solid ${alpha(muiTheme.palette.text.primary, 0.2)}`,
    borderRadius: 2,
    p: 1,
    transition: 'all 0.3s ease',
    '&:hover': {
      bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
      borderColor: muiTheme.palette.primary.main,
      transform: 'scale(1.05)',
      '& .theme-icon': {
        transform: 'rotate(180deg)'
      }
    },
    '& .theme-icon': {
      transition: 'transform 0.3s ease'
    },
    ...sx
  };

  const buttonSx = {
    minWidth: 120,
    height: 40,
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    border: `1px solid ${alpha(muiTheme.palette.text.primary, 0.2)}`,
    color: muiTheme.palette.text.primary,
    bgcolor: 'transparent',
    transition: 'all 0.3s ease',
    '&:hover': {
      bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
      borderColor: muiTheme.palette.primary.main,
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${alpha(muiTheme.palette.primary.main, 0.2)}`
    },
    ...sx
  };

  const switchSx = {
    width: 60,
    height: 30,
    borderRadius: 15,
    bgcolor: isDarkMode ? muiTheme.palette.primary.main : alpha(muiTheme.palette.text.primary, 0.2),
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 3,
      left: isDarkMode ? 33 : 3,
      width: 24,
      height: 24,
      borderRadius: '50%',
      bgcolor: 'white',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: isDarkMode ? 8 : 40,
      transform: 'translateY(-50%)',
      fontSize: '12px',
      color: 'white',
      transition: 'all 0.3s ease'
    },
    ...sx
  };

  const renderIconButton = () => (
    <IconButton
      onClick={toggleTheme}
      size={size}
      sx={iconButtonSx}
    >
      <Box className="theme-icon">
        {getIcon()}
      </Box>
    </IconButton>
  );

  const renderButton = () => (
    <Box
      component="button"
      onClick={toggleTheme}
      sx={buttonSx}
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      <Box className="theme-icon">
        {getIcon()}
      </Box>
      {isDarkMode ? 'Light' : 'Dark'}
    </Box>
  );

  const renderSwitch = () => (
    <Box
      onClick={toggleTheme}
      sx={switchSx}
    />
  );

  const renderContent = () => {
    switch (variant) {
      case 'button':
        return renderButton();
      case 'switch':
        return renderSwitch();
      default:
        return renderIconButton();
    }
  };

  if (showTooltip && variant === 'icon') {
    return (
      <Tooltip title={getTooltipText()} arrow>
        {renderContent()}
      </Tooltip>
    );
  }

  return renderContent();
};

export default ThemeToggle;
